import { useState, useCallback, useEffect, useRef, useLayoutEffect } from 'react';
import { Accordion } from '@components';
import { content } from '@content';
import { Box } from '@material-ui/core';
import Button from '@material-ui/core/Button';
import { deployment as deploymentModule } from '@modules';
import { useAppDispatch } from '@store';
import Cursor from '@utils/cursor';

import { PersonalizationPanel } from './PersonalizationPanel';
import { SLPInput } from './SLPInput';
// import { SubjectLinePreheaderProps } from './SubjectLinePreheader.props';
import { useStyles } from './SubjectLinePreheader.styles';
import { parseAndCleanHTML } from './SubjectLinePreheader.util';

import { SubjectLinePreheaderContext, SubjectLinePreheaderContextProps } from './SubjectLinePreheaderContext';
import { SubjectLinePreheaderProps } from './SubjectLinePreheader.props';

export const SubjectLinePreheader = ({ resetComponent, setResetComponent }: SubjectLinePreheaderProps): JSX.Element => {
  const styles = useStyles();
  const dispatch = useAppDispatch();

  const deployment = deploymentModule.useDeploymentData();
  const deploymentId = deployment?.id || '';
  const subject = deployment?.subjectLine || '';
  const preheader = deployment?.preheader || '';

  const [isEditMode, setIsEditMode] = useState(false);
  const [isFormEdited, setIsFormEdited] = useState(false);
  const [editedSubjectLine, setEditedSubjectLine] = useState(subject);
  const [editedPreheader, setEditedPreheader] = useState(preheader);
  const [initialSubjectLine] = useState(subject);
  const [initialPreheader] = useState(preheader);

  const [isSaving, setIsSaving] = useState(false);

  const [updateSignalList, setUpdateSignalList] = useState(false);

  // Accordion expanded states
  const [isSLPExpanded, setIsSLPExpanded] = useState(false);

  // Personalization Token Target
  const [targetInputFieldId, setPersonalizationTargetInputId] = useState<string | undefined>(undefined);

  // cursor position in subject line or preheader field
  const [cursorPosition, setCursorPosition] = useState<number>(0);

  // outer container ref
  const outerContainerRef = useRef<HTMLDivElement>(null);
  const [panelLeftPosition, setPanelLeftPosition] = useState<string>('0px');
  const [panelWidth, setPanelWidth] = useState<string>('0px');

  useEffect(() => {
    if (!isSaving) {
      setEditedSubjectLine(subject);
    }
  }, [isSaving, subject]);

  useEffect(() => {
    if (!isSaving) {
      setEditedPreheader(preheader);
    }
  }, [isSaving, preheader]);

  // reset the component state when the resetComponent prop changes to true
  useEffect(() => {
    if (resetComponent) {
      setIsEditMode(false);
      setIsSLPExpanded(false);
      setResetComponent(false);
    }
  }, [resetComponent, setResetComponent]);

  // ===========================================================================
  // These functions may need to be refactored into calling functions as appropriate
  // const onSubjectLinePreheaderSave = async (subject: string, preheader: string) => {
  const onSave = useCallback(
    async (newSubject: string, newPreheader: string) => {
      // clean up the inputs
      const cleanSubject = parseAndCleanHTML(newSubject);
      const cleanPreheader = parseAndCleanHTML(newPreheader);
      setEditedSubjectLine(cleanSubject);
      setEditedPreheader(cleanPreheader);

      // create a form data object anbd append fields
      const formData = new FormData();
      formData.append('subjectLine', cleanSubject);
      formData.append('preheader', cleanPreheader);

      // Save subject line and preheader
      if (deploymentId !== undefined && typeof deploymentId === 'number' && !isNaN(deploymentId)) {
        await dispatch(deploymentModule.thunk.update({ deployment: deploymentId, body: formData }));
        setIsSaving(false);
      }
    },
    [deploymentId, dispatch],
  );

  const onCancel = useCallback(() => {
    setPersonalizationTargetInputId(undefined);
    setEditedSubjectLine(deployment?.subjectLine || '');
    setEditedPreheader(deployment?.preheader || '');
  }, [deployment?.preheader, deployment?.subjectLine]);

  const onCursorPositionChange = useCallback((target: HTMLDivElement | undefined, position: number) => {
    // setPtokenInputTarget(target);
    setCursorPosition(position);
  }, []);
  // ===========================================================================

  const handleSave = useCallback(() => {
    // disable edit mode
    setIsFormEdited(false);
    setIsEditMode(false);

    onCursorPositionChange(undefined, -1);

    // Save
    setIsSaving(true);
    onSave(editedSubjectLine, editedPreheader);
  }, [editedPreheader, editedSubjectLine, onCursorPositionChange, onSave]);

  const handleTextInputClick = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      const targetName = event.currentTarget.getAttribute('data-name');

      if (isEditMode && targetName) {
        setPersonalizationTargetInputId(targetName);
      }
    },
    [isEditMode],
  );

  const handleTextInputDoubleClick = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      const targetName = event.currentTarget.getAttribute('data-name');

      if (!isEditMode && targetName) {
        setIsEditMode(true);
        setIsFormEdited(false);

        if (targetName === 'editor-subjectLine') {
          // set the target field to edit to the parent
          setPersonalizationTargetInputId(targetName);
        } else if (targetName === 'editor-preheader') {
          // set the target field to edit to the parent
          setPersonalizationTargetInputId(targetName);
        } else {
          setIsEditMode(false);
        }
      }
    },
    [isEditMode],
  );

  useEffect(() => {
    if (isEditMode) {
      document.getElementById(targetInputFieldId || '')?.focus();
    }
  }, [isEditMode, targetInputFieldId]);

  const handleCancel = useCallback(() => {
    setEditedSubjectLine(subject);
    setEditedPreheader(preheader);

    setIsEditMode(false);
    setIsFormEdited(false);

    onCursorPositionChange(undefined, -1);
    onCancel();
  }, [onCancel, onCursorPositionChange, preheader, subject]);

  const handleInputChange = useCallback(
    (currentTarget: HTMLDivElement) => {
      const { innerHTML } = currentTarget;
      let position = -1;

      if (document.activeElement === currentTarget) {
        position = Cursor.getCursorPosition(currentTarget);

        // pass the updated cursor position to TopBar
        onCursorPositionChange(currentTarget, position);
      }

      switch (currentTarget.id) {
        case 'editor-subjectLine':
          if (innerHTML !== subject) {
            setEditedSubjectLine(innerHTML);
            setIsFormEdited(true);
            setUpdateSignalList(true);
          }
          break;
        case 'editor-preheader':
          if (innerHTML !== preheader) {
            setEditedPreheader(innerHTML);
            setIsFormEdited(true);
            setUpdateSignalList(true);
          }
          break;
        default:
          break;
      }

      if (document.activeElement === currentTarget) {
        setTimeout(() => {
          Cursor.setCursorPosition(currentTarget, position);
        }, 0);
      }
    },
    [onCursorPositionChange, preheader, subject],
  );

  // ignore carriage returns
  const handleKeyDown = useCallback((event: React.KeyboardEvent<HTMLDivElement>) => {
    // const currentTarget = event.currentTarget;
    // if (document.activeElement === currentTarget) {
    //   return;
    // }
  }, []);

  // Check if the edited values are the same as the initial values
  useEffect(() => {
    if (initialSubjectLine === subject && initialPreheader === preheader) {
      setIsFormEdited(false);
    } else {
      setIsFormEdited(true);
    }
  }, [initialPreheader, initialSubjectLine, preheader, subject]);

  const onSignalDelete = useCallback(
    (signalHtml) => {
      if (isEditMode) {
        switch (targetInputFieldId) {
          case 'editor-subjectLine':
            setEditedSubjectLine(signalHtml);
            setIsFormEdited(true);
            break;
          case 'editor-preheader':
            setEditedPreheader(signalHtml);
            setIsFormEdited(true);
            break;
          default:
            break;
        }
      }
    },
    [isEditMode, targetInputFieldId],
  );

  const onSignalInsert = useCallback(
    (chosenElem: HTMLTextAreaElement | HTMLDivElement, signalHtml: string) => {
      const id = chosenElem.id;

      //todo: may be necessary to remove all &nbsp; from the html string before splitting
      const html = [
        chosenElem.innerHTML.replaceAll('&amp;', `&`).replaceAll('&nbsp;', ` `).slice(0, cursorPosition),
        signalHtml,
        chosenElem.innerHTML.replaceAll('&amp;', `&`).replaceAll('&nbsp;', ` `).slice(cursorPosition),
      ].join('');

      switch (id) {
        case 'editor-subjectLine':
          setEditedSubjectLine(html);
          setIsFormEdited(true);
          break;
        case 'editor-preheader':
          setEditedPreheader(html);
          setIsFormEdited(true);
          break;
        default:
          break;
      }
    },
    [cursorPosition],
  );

  const onSignalUpdate = useCallback(
    (signalHtml: string) => {
      if (isEditMode) {
        switch (targetInputFieldId) {
          case 'editor-subjectLine':
            setEditedSubjectLine(signalHtml);
            setIsFormEdited(true);
            break;
          case 'editor-preheader':
            setEditedPreheader(signalHtml);
            setIsFormEdited(true);
            break;
          default:
            break;
        }
      }
    },
    [isEditMode, targetInputFieldId],
  );

  const slpContextValues: SubjectLinePreheaderContextProps = {
    isFormEdited,
    targetInputFieldId,
    updateSignalList,
    onSignalInsert,
    onSignalDelete,
    onSignalUpdate,
    setIsFormEdited,
    setUpdateSignalList,
  };

  const getPersonalizationPanelLeftPosition = useCallback(() => {
    if (outerContainerRef.current) {
      const rect = outerContainerRef.current.getBoundingClientRect();
      setPanelLeftPosition(rect.right + 17 - 76 + 'px'); // 17px to the right of outerContainer, 76px is the difference between the width of the personalization panel and the right side panel
      setPanelWidth('calc(100vw - ' + rect.right + 'px - 17px - 1.6rem)'); // 17px to the right of outerContainer, 1.6rem is the right margin of the personalization panel
    }
  }, []);

  useLayoutEffect(() => {
    getPersonalizationPanelLeftPosition();
  }, [getPersonalizationPanelLeftPosition, isEditMode, isSLPExpanded]);

  useEffect(() => {
    const handleResize = () => {
      getPersonalizationPanelLeftPosition();
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Box className={`${styles.subjectLinePreheaderWrapper} ${isSLPExpanded ? styles.slpExpanded : ''}`}>
      <div className={styles.outerContainer} ref={outerContainerRef}>
        <div className={styles.subjectLinePreheaderContent}>
          <Accordion
            expanded={isSLPExpanded}
            onChange={() => {
              setIsSLPExpanded(!isSLPExpanded);
              setIsEditMode(false);
            }}
            title={content.subjectLineAndPreheader}
            uiType="quaternary"
          >
            <div className={styles.inputRow}>
              <SLPInput
                id="editor-subjectLine"
                isEditMode={isEditMode}
                label={content.subjectLine}
                initialValue={editedSubjectLine}
                onInput={handleInputChange}
                onKeyDown={handleKeyDown}
                onClick={handleTextInputClick}
                onDoubleClick={handleTextInputDoubleClick}
                onCursorPositionChange={onCursorPositionChange}
                setValue={setEditedSubjectLine}
              />
            </div>
            <div className={styles.inputRow}>
              <SLPInput
                id="editor-preheader"
                isEditMode={isEditMode}
                label={content.preheader}
                initialValue={editedPreheader}
                onInput={handleInputChange}
                onKeyDown={handleKeyDown}
                onClick={handleTextInputClick}
                onDoubleClick={handleTextInputDoubleClick}
                onCursorPositionChange={onCursorPositionChange}
                setValue={setEditedPreheader}
              />
            </div>
            {isEditMode && (
              <div className={styles.buttonContainer}>
                <Button
                  variant="outlined"
                  color="primary"
                  onClick={handleCancel}
                  className={[styles.button, styles.cancelButton].join(' ')}
                >
                  {content.cancel}
                </Button>

                <Button
                  variant="contained"
                  color="secondary"
                  disabled={!isFormEdited}
                  onClick={handleSave}
                  className={[styles.button, styles.saveButton].join(' ')}
                >
                  {content.save}
                </Button>
              </div>
            )}
          </Accordion>
        </div>
      </div>
      {isEditMode && isSLPExpanded && (
        <SubjectLinePreheaderContext.Provider value={slpContextValues}>
          <PersonalizationPanel
            className={styles.personalizationPanel}
            leftPosition={panelLeftPosition}
            width={panelWidth}
          />
        </SubjectLinePreheaderContext.Provider>
      )}
    </Box>
  );
};

SubjectLinePreheader.displayName = 'SubjectLinePreheader';
